import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';
import type { CercharGeneral, CercharStylus } from '../../../types/rockCerchar';
// Shared section idiom (reused read-only from the Rock TCS / BTS modules)
import { DerivedValue, inputSx, paperSx, SectionHeader, unit } from '../rock-tcs/sectionStyles';
import {
  CERCHAR_SPECIMEN_CONDITIONS,
  CERCHAR_STYLUS_HRC,
  CERCHAR_SURFACE_CONDITIONS,
  CERCHAR_TEST_EQUIPMENT,
  CERCHAR_TEST_VELOCITY,
} from './rockCercharFields';

interface Props {
  general: CercharGeneral;
  styluses: CercharStylus[];
  onGeneralChange: (field: keyof CercharGeneral, value: CercharGeneral[keyof CercharGeneral]) => void;
  onStylusChange: (
    stylusNumber: number,
    field: keyof CercharStylus,
    value: CercharStylus[keyof CercharStylus],
  ) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

/**
 * Cerchar — Test Parameters tab. SPECIMEN DETAILS (REQ-01/02 + the two Leeb
 * hardness readings) and the fixed standard parameters read-only block
 * (D4 / REQ-06). The per-stylus measurements live on the Result Entry tab.
 *
 * BUG-2026-0142: the Test Method field and the Equipment section (Cerchar
 * Machine, Leeb Hardness Gauge, Calibration Certificate, Additional
 * Equipment / Accessories) were removed from the UI per lab review — the
 * stored columns remain untouched and none of them print on the report.
 * The Leeb Hardness (Ends)/(Edge) inputs moved up into this section, and
 * "Test Method Setup" was renamed "Specimen Details".
 */
export const RockCercharParametersTab = ({ general, onGeneralChange, readOnly, workOrderDetailKey }: Props) => {
  const num = (field: keyof CercharGeneral) => (value: number | null) => onGeneralChange(field, value);

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5, maxWidth: 980 }}>
      {/* ── SPECIMEN DETAILS (REQ-01 / REQ-02 + Leeb hardness) ── */}
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Specimen Details" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
          <TextField
            select size="small" label="Specimen Condition"
            value={general.specimenCondition ?? ''}
            onChange={(e) => onGeneralChange('specimenCondition', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            {CERCHAR_SPECIMEN_CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Test Surface Condition"
            value={general.testSurfaceCondition ?? ''}
            onChange={(e) => onGeneralChange('testSurfaceCondition', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            {CERCHAR_SURFACE_CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            size="small" label="Plane of Weakness — orientation"
            value={general.planeOfWeaknessOrientation ?? ''}
            onChange={(e) => onGeneralChange('planeOfWeaknessOrientation', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
            helperText="Orientation w.r.t. core axis (REQ-01)"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, maxWidth: 480, mb: 1.5 }}>
          <NumericTextField
            size="small" label="Leeb Hardness (Ends)"
            value={general.leebHardnessEnds} onChange={num('leebHardnessEnds')}
            InputProps={{ endAdornment: unit('HLD'), readOnly }} sx={inputSx}
          />
          <NumericTextField
            size="small" label="Leeb Hardness (Edge)"
            value={general.leebHardnessEdge} onChange={num('leebHardnessEdge')}
            InputProps={{ endAdornment: unit('HLD'), readOnly }} sx={inputSx}
          />
        </Box>

        {/* Stylus Tips Checked for Sharpness — Yes/No toggle (REQ-02) */}
        <FormControl sx={{ mb: 1.5 }}>
          <FormLabel sx={{ fontSize: '0.78rem', mb: 0.25 }}>
            Stylus Tips Checked for Sharpness
          </FormLabel>
          <RadioGroup
            row
            value={general.stylusTipsChecked === null || general.stylusTipsChecked === undefined
              ? ''
              : general.stylusTipsChecked ? 'yes' : 'no'}
            onChange={(e) => {
              if (readOnly) return;
              onGeneralChange('stylusTipsChecked', e.target.value === 'yes');
            }}
          >
            <FormControlLabel
              value="yes" disabled={readOnly}
              control={<Radio size="small" />}
              label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Yes</Typography>}
            />
            <FormControlLabel
              value="no" disabled={readOnly}
              control={<Radio size="small" />}
              label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>No</Typography>}
            />
          </RadioGroup>
        </FormControl>

        <TextField
          size="small" label="Remarks" fullWidth multiline minRows={2}
          value={general.remarksText ?? ''}
          onChange={(e) => onGeneralChange('remarksText', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        />
      </Paper>

      {/* ── Additional Observations — lab-internal, never printed ── */}
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Additional Observations (internal — not printed on the report)" />
        <TextField
          size="small" fullWidth multiline minRows={3}
          placeholder="Internal observations about specimen, test conditions, or equipment — for the lab and reviewer only."
          value={general.additionalObservations ?? ''}
          onChange={(e) => onGeneralChange('additionalObservations', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        />
        {workOrderDetailKey && (
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        )}
      </Paper>

      {/* ── Fixed standard parameters (D4 / REQ-06) — read-only info block ── */}
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Standard Parameters (fixed)" color="text.secondary" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.25 }}>
          <DerivedValue label="Test Equipment" value={CERCHAR_TEST_EQUIPMENT} />
          <DerivedValue label="Test Velocity" value={CERCHAR_TEST_VELOCITY} />
          <DerivedValue label="Stylus Hardness" value={CERCHAR_STYLUS_HRC} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', display: 'block', mt: 0.75 }}>
          Fixed per the lab's Cerchar standard (ASTM D7625) — echoed to the report.
        </Typography>
      </Paper>
    </Box>
  );
};
